import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import FullScreenLoader from "../components/FullScreenLoader";

const VERIFY_SESSION_URL =
  (typeof import.meta !== "undefined" &&
    import.meta.env?.VITE_VERIFY_SESSION_URL) ||
  process.env.REACT_APP_VERIFY_SESSION_URL ||
  "/verify-checkout-session";

const REGISTER_URL =
  "https://printlogs-17738dc65798.herokuapp.com/bizVantage/register";

const CheckoutResult = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // 'success' | 'canceled' | 'failed'
  const [message, setMessage] = useState("");

  useEffect(() => {
    const qs = new URLSearchParams(search);
    const sessionId =
      qs.get("session_id") || qs.get("sessionId") || qs.get("session");
    const success = qs.get("success");
    const canceled =
      qs.get("canceled") || qs.get("cancelled") || qs.get("canceled");

    async function verify() {
      if (sessionId) {
        try {
          setLoading(true);
          const resp = await axios.post(
            VERIFY_SESSION_URL,
            { sessionId },
            { headers: { "Content-Type": "application/json" } },
          );
          const data = resp?.data || {};

          // server should return a status field or boolean paid
          if (
            data?.paid ||
            data?.status === "complete" ||
            data?.status === "paid"
          ) {
            // complete registration if pending signup exists
            setStatus("success");
            setMessage(data?.message || "Payment completed successfully.");
            const pending = localStorage.getItem("bv.pendingSignup");
            if (pending) {
              try {
                setLoading(true);
                const body = JSON.parse(pending);
                const resp = await axios.post(REGISTER_URL, body, {
                  headers: { "Content-Type": "application/json" },
                });
                const reg = resp?.data || {};
                if (reg?.success) {
                  localStorage.setItem(
                    "bv.registerResponse",
                    JSON.stringify(reg),
                  );
                  setMessage(
                    reg?.message || "Account created and subscription active.",
                  );
                  localStorage.removeItem("bv.pendingSignup");
                } else {
                  setStatus("failed");
                  setMessage(
                    reg?.message ||
                      "Payment succeeded but account creation failed.",
                  );
                }
              } catch (err) {
                console.error(
                  "complete registration error",
                  err?.response || err,
                );
                setStatus("failed");
                setMessage(
                  "Payment succeeded but registration failed. Contact support.",
                );
              } finally {
                setLoading(false);
              }
            }
          } else if (data?.status === "open" || data?.status === "unpaid") {
            setStatus("failed");
            setMessage(data?.message || "Payment is pending or incomplete.");
          } else if (
            data?.status === "canceled" ||
            data?.status === "expired"
          ) {
            setStatus("canceled");
            setMessage(data?.message || "Payment was canceled.");
          } else {
            // unknown - treat as success if server indicates ok
            setStatus(data?.status || "success");
            setMessage(data?.message || "Checkout completed.");
          }
        } catch (err) {
          console.error("verify session error", err?.response || err);
          setMessage(
            "Unable to verify checkout session. If you were charged, please contact support.",
          );
          // fall back to query params
          if (success === "true" || success === "1") setStatus("success");
          else if (canceled) setStatus("canceled");
          else setStatus("failed");
        } finally {
          setLoading(false);
        }
      } else {
        // no session id - use query flags
        if (success === "true" || success === "1") {
          setStatus("success");
          setMessage("Payment completed successfully.");
        } else if (canceled) {
          setStatus("canceled");
          setMessage("Payment was canceled.");
        } else {
          setStatus("failed");
          setMessage("Payment status unknown. Contact support if needed.");
        }
        setLoading(false);
      }
    }

    verify();
  }, [search]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-slate-900 to-slate-800">
      {loading && <FullScreenLoader />}

      {!loading && (
        <div className="max-w-lg w-full">
          <div className="bg-white/5 border border-white/10 p-6 rounded-lg shadow-lg text-white">
            <div className="flex items-center gap-4 mb-4">
              <div
                className={`h-12 w-12 flex items-center justify-center rounded-full ${status === "success" ? "bg-emerald-500" : status === "canceled" ? "bg-amber-500" : "bg-rose-500"}`}
              >
                {status === "success" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : status === "canceled" ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white animate-pulse"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3"
                    />
                  </svg>
                )}
              </div>
              <div>
                <h3 className="text-lg font-semibold">
                  {status === "success"
                    ? "Payment Successful"
                    : status === "canceled"
                      ? "Payment Canceled"
                      : "Payment Issue"}
                </h3>
                <p className="text-sm text-white/80">{message}</p>
              </div>
            </div>

            <div className="mt-4 flex gap-3">
              <Button onClick={() => navigate("/dashboard")}>
                Go to Dashboard
              </Button>
              <Button variant="ghost" onClick={() => navigate(-1)}>
                Back
              </Button>
            </div>

            <div className="mt-4 text-xs text-white/60">
              {status === "success" ? (
                <p>
                  Thank you — your subscription is active. You can manage
                  billing in Settings.
                </p>
              ) : status === "canceled" ? (
                <p>
                  If you changed your mind, you can try again from the Pricing
                  page.
                </p>
              ) : (
                <p>
                  If you were charged but see an error, please contact support
                  with your order details.
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckoutResult;
